<template>
  <div class="create-chat">
    <ElDialog
      v-model="isShow"
      :title="$t('whatsapp.chat.createSingleTitle')"
      :width="412"
      destroy-on-close
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @close="handleCancel"
    >
      <div class="create-chat__body">
        <div class="search-row">
          <ElInput
            v-model="whatsAppNumber"
            :placeholder="$t('whatsapp.chat.placeholder.inputWhatsappNumber')"
            clearable
            size="small"
            class="number-input"
            @blur="whatsAppNumber = normalizeWhatsAppId(whatsAppNumber)"
          />
          <ElButton
            class="btn-query"
            size="small"
            :loading="searchingAccount"
            :disabled="!normalizedInput"
            @click="handleQueryAccount"
          >
            查询
          </ElButton>
        </div>

        <div class="contact-sections" @scroll="handleContactsScroll">
          <div class="contact-section">
            <div class="section-title">联系人</div>
            <div
              v-for="item in displayContacts"
              :key="item.__key"
              class="contact-item"
              :class="{ 'contact-item--highlight': highlightContactId && String(item?.id || '') === String(highlightContactId) }"
              @click="handlePick(item)"
            >
              <el-avatar :size="36" :src="item.avatar">
                {{ item.name?.charAt(0) || "?" }}
              </el-avatar>
              <span class="contact-name">
                {{ item.name }}
                <!-- {{ item.phone && item.phone !== item.name ? ` ${item.phone}` : "" }} -->
              </span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="create-chat__footer">
          <ElButton class="btn-cancel" size="small" @click="handleCancel">
            {{ $t('common.cancel') }}
          </ElButton>
          <ElButton
            class="btn-confirm"
            type="primary"
            size="small"
            @click="handleCreate"
          >
            {{ $t('whatsapp.chat.create') }}
          </ElButton>
        </div>
      </template>
    </ElDialog>
  </div>
</template>

<script>
import { getAllContact, getLidAndUsByPhone, getUsByLid } from "@/api/whatsapp";
import { normalizeWhatsAppId } from "@/utils/whatsapp";

export default {
  name: "CreateChat",
  emits: ["create"],
  data() {
    return {
      isShow: false,
      whatsAppNumber: "",
      contactsInternal: [],
      loadingContacts: false,
      contactsHasMore: true,
      contactsPagination: {
        limit: 10,
        offset: 1,
        sortBy: "",
        sortOrder: "",
      },
      searchingAccount: false,
      queriedAccountItem: null,
      highlightContactId: "",
      _filterTimer: null,
    };
  },
  computed: {
    normalizedInput() {
      const v = this.whatsAppNumber && String(this.whatsAppNumber).trim();
      if (!v) return "";
      return normalizeWhatsAppId(v);
    },
    filteredContacts() {
      const kw = String(this.normalizedInput || "").trim();
      if (!kw) return this.contactsInternal || [];
      const low = kw.toLowerCase();
      return (this.contactsInternal || []).filter((c) => {
        const name = String(c?.name || "").toLowerCase();
        const phone = String(c?.phone || "");
        const id = String(c?.id || "");
        return name.includes(low) || phone.includes(kw) || id.includes(kw);
      });
    },
    displayContacts() {
      const list = this.filteredContacts || [];
      const head = this.queriedAccountItem ? [this.queriedAccountItem] : [];

      const merged = [...head, ...list].map((it) => ({
        ...it,
        __key: String(it?.__key || it?.id || it?.phone || Math.random()),
      }));

      // 去重：优先按 phone 去重（做 normalize）；phone 为空则按 id 去重
      const map = new Map();
      merged.forEach((it) => {
        const normalizedPhone = this.normalizePhoneInput(it?.phone);
        const pnPhone = it?.pn ? this.extractPhoneFromJid(it?.pn) : ''
        const id = String(it?.id || '');
        const idPhone = id && id.endsWith('@c.us') ? this.extractPhoneFromJid(id) : ''
        const lid = String(it?.lid || '')
        const key = String(normalizedPhone || pnPhone || idPhone || lid || id || it?.__key || '');
        if (!key) return;
        if (!map.has(key)) {
          map.set(key, it);
        }
      });
      return Array.from(map.values());
    },
  },
  watch: {
    whatsAppNumber() {
      this.highlightContactId = "";
      if (this._filterTimer) {
        clearTimeout(this._filterTimer);
        this._filterTimer = null;
      }
      this._filterTimer = setTimeout(() => {
        this.queriedAccountItem = null;
      }, 200);
    },
  },
  methods: {
    normalizeWhatsAppId,
    open() {
      this.isShow = true;
      this.whatsAppNumber = "";
      this.queriedAccountItem = null;
      this.searchingAccount = false;

      this.contactsPagination.offset = 1;
      this.contactsHasMore = true;
      this.contactsInternal = [];
      this.loadContacts();
    },
    handleCancel() {
      this.isShow = false;
    },
    handleContactsScroll(e) {
      if (this.loadingContacts) return;
      if (!this.contactsHasMore) return;

      const el = e?.target;
      if (!el) return;

      const threshold = 40;
      const reachBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
      if (reachBottom) {
        this.loadContacts();
      }
    },
    mapContactItem(item) {
      const id = item?.id ?? item?.contactId ?? item?._id;
      const phoneRaw = item?.number ?? item?.phone ?? item?.mobile ?? "";
      const phone = this.normalizePhoneInput(phoneRaw);
      const name =
        item?.name ||
        item?.pushname ||
        item?.pushName ||
        item?.verifiedName ||
        item?.shortName ||
        phone ||
        "";
      const avatar =
        item?.avatar ??
        item?.avatarUrl ??
        item?.profilePicUrl ??
        item?.picture ??
        item?.picUrl ??
        "";
      return { ...item, id, name, avatar, phone };
    },
    async loadContacts() {
      if (this.loadingContacts) return;
      if (!this.contactsHasMore) return;

      const params = {
        limit: this.contactsPagination.limit,
        offset: this.contactsPagination.offset,
        sortBy: this.contactsPagination.sortBy,
        sortOrder: this.contactsPagination.sortOrder,
      };

      try {
        this.loadingContacts = true;
        const res = await getAllContact(params);
        const list = res?.result || res?.data?.result || res?.data || [];

        if (Array.isArray(list) && list.length > 0) {
          const mapped = list.map((it) => this.mapContactItem(it));
          const exist = new Set((this.contactsInternal || []).map((c) => c?.phone || c?.id));
          const newItems = mapped.filter((c) => {
            const key = c?.phone || c?.id;
            if (!key) return false;
            if (exist.has(key)) return false;
            exist.add(key);
            return true;
          });
          this.contactsInternal = [...(this.contactsInternal || []), ...newItems];

          const hasMore = list.length === this.contactsPagination.limit;
          this.contactsHasMore = hasMore;
          if (hasMore) {
            this.contactsPagination.offset += 1;
          }
        } else {
          this.contactsHasMore = false;
        }
      } catch (err) {
        this.contactsHasMore = false;
      } finally {
        this.loadingContacts = false;
      }
    },
    normalizePhoneInput(v) {
      const s = String(v || "").trim();
      if (!s) return "";
      return normalizeWhatsAppId(s);
    },
    extractPhoneFromJid(jid) {
      const id = String(jid || "").trim();
      if (!id) return "";
      const m = id.match(/^(\d+)(?=@)/);
      return m && m[1] ? String(m[1]) : "";
    },
    async handlePick(item) {
      let phone = this.normalizePhoneInput(item?.phone);
      if (!phone) {
        const id = String(item?.id || "").trim();
        if (id.endsWith("@c.us")) {
          phone = this.extractPhoneFromJid(id);
        } else if (id.endsWith("@lid") && typeof getUsByLid === "function") {
          try {
            const res = await getUsByLid({ lid: id });
            const result = res?.result || res?.data?.result || null;
            const pn = String(result?.pn || "").trim();
            if (pn) {
              phone = this.extractPhoneFromJid(pn);
            }
          } catch (e) {
          }
        }
      }

      if (!phone) return;
      const isFromContacts = (this.contactsInternal || []).some((c) => String(c?.id || '') === String(item?.id || ''));
      const payload = isFromContacts
        ? {
            phone,
            contact: {
              name: String(item?.name || phone),
              avatar: String(item?.avatar || ""),
            },
          }
        : {
            phone,
            contact: {
              name: phone,
              avatar: "",
            },
          };

      this.$emit("create", payload);
      this.isShow = false;
    },
    async handleQueryAccount() {
      const value = this.normalizedInput;
      if (!value) return;
      if (this.searchingAccount) return;

      try {
        this.searchingAccount = true;
        const res = await getLidAndUsByPhone({ phone: value });
        const result = res?.result || res?.data?.result || null;
        const lid = result?.lid || "";
        const pn = result?.pn || "";

        if (!lid) {
          this.$message?.warning?.("该手机号未注册 WhatsApp 或不可用");
          this.queriedAccountItem = null;
          return;
        }

        const matchedContact = (this.contactsInternal || []).find((c) => {
          const cId = String(c?.id || '').trim()
          const cPhone = this.normalizePhoneInput(c?.phone)
          const cIdPhone = cId && cId.endsWith('@c.us') ? this.extractPhoneFromJid(cId) : ''
          const byPhone = cPhone && (cPhone === value || cPhone.includes(value) || value.includes(cPhone))
          const byId = (lid && cId && String(cId) === String(lid)) || (pn && cId && String(cId) === String(pn))
          const byIdPhone = cIdPhone && (cIdPhone === value || cIdPhone.includes(value) || value.includes(cIdPhone))
          return byPhone || byId || byIdPhone
        });

        if (matchedContact) {
          const idx = (this.contactsInternal || []).findIndex((c) => String(c?.id || '') === String(matchedContact?.id || ''))
          if (idx >= 0) {
            const next = {
              ...(this.contactsInternal[idx] || {}),
              phone: this.normalizePhoneInput(matchedContact?.phone) || this.extractPhoneFromJid(matchedContact?.id) || value,
              lid: matchedContact?.lid || lid,
              pn: matchedContact?.pn || pn,
            }
            const arr = [...(this.contactsInternal || [])]
            arr.splice(idx, 1, next)
            this.contactsInternal = [next, ...arr.filter((_, i) => i !== idx)]
          }
          this.queriedAccountItem = null;
          this.highlightContactId = String(matchedContact?.id || '');
        } else {
          this.queriedAccountItem = {
            __key: `query_${lid || pn || value}`,
            id: lid || pn || value,
            name: value,
            avatar: "",
            phone: value,
            lid,
            pn,
          };
          this.highlightContactId = "";
        }
      } catch (e) {
        this.$message?.error?.(e?.message || "查询账号失败");
        this.queriedAccountItem = null;
        this.highlightContactId = "";
      } finally {
        this.searchingAccount = false;
      }
    },
    async handleInput(value) {
      value = String(value || "").trim();
      if (!value) {
        this.queriedAccountItem = null;
        return;
      }
      value = normalizeWhatsAppId(value);

      const matchedContact = (this.contactsInternal || []).find((c) => {
        const p = this.normalizePhoneInput(c?.phone)
        return p && (p === value || p.includes(value) || value.includes(p))
      })

      const payload = matchedContact
        ? {
            phone: value,
            contact: {
              name: String(matchedContact?.name || value),
              avatar: String(matchedContact?.avatar || ""),
            },
          }
        : {
            phone: value,
            contact: {
              name: value,
              avatar: "",
            },
          }

      this.$emit("create", payload);
      this.isShow = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.create-chat {
  display: inline-block;
}

.create-chat__body {
  padding: 8px 0 0;

  .search-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .number-input {
    flex: 1;
    min-width: 0;
  }

  .btn-query {
    flex: 0 0 auto;
    min-width: 56px;
  }

  .contact-sections {
    margin-top: 12px;
    max-height: 420px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .contact-section {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .section-title {
    font-size: 12px;
    color: #999;
    margin-bottom: 8px;
  }

  .contact-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
    cursor: pointer;
    gap: 12px;

    &.contact-item--highlight {
      background: #e6f7ff;
      margin: 0 -12px;
      padding: 8px 12px;
    }

    &:hover {
      background: #f5f5f5;
      margin: 0 -12px;
      padding: 8px 12px;
    }
  }

  .contact-name {
    flex: 1;
    font-size: 14px;
    color: #333;
  }
}

.create-chat__footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 4px;

  .btn-cancel {
    min-width: 65px;
  }

  .btn-confirm {
    min-width: 65px;
    background: #1890FF !important;
  }
}
</style>